import { ArrowRight } from "@mui/icons-material";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import { format } from "date-fns";
import { Fragment, useState } from "react";
import { Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { leadingZerosDateFormat } from "../../../../common/helpers/formatDateTime";
import isEmpty from "../../../../common/helpers/isEmpty";
import {
  SalaryIncreaseStatus,
  SalaryWarningType,
  ShiftSalaryRow,
} from "../../types/common";
import { MassUpdateStatusRowId } from "../../types/queries";
import {
  formatTesSalaryTableSelectionSummary,
  renderRowStatus,
  uiRowToId,
} from "../helpers";
import BulkSalaryIncreaseCollapsedRows from "./BulkSalaryIncreaseCollapsedRows";
import SalaryTableCells from "./SalaryTableCells";


type Props = {
  row: ShiftSalaryRow;
  index: number;
  raiseDateSalaryKeys: string[];
  afterRaiseDateSalaryPerDateKeys: string[];
  isSelected?: boolean;
  onSelect?: () => void;
  toggleUnclear: (rowId: MassUpdateStatusRowId) => void;
};

const BulkSalaryIncreaseShiftSalaryRow = ({
  row,
  index,
  raiseDateSalaryKeys,
  afterRaiseDateSalaryPerDateKeys,
  isSelected,
  onSelect,
  toggleUnclear
}: Props) => {
  const [open, setOpen] = useState(false);

  const mainRowBackground = index % 2 === 0 ? "#fff" : "#FAFAFB";

  const setRowClearance = (row: ShiftSalaryRow) => {
    // console.log(`row ${uiRowToId(row)} is now unclear ${row.unclear}`)
    toggleUnclear(uiRowToId(row));
  }

  const warningText = (
    <Box>
      {row.warnings?.map((w, idx) => (
        <Trans
          key={`${w.type}-${idx}`}
          i18nKey={`warnings.${w.type}`}
          ns="massSalaryIncrease"
          values={w.params}
        />
      ))}
    </Box>
  );
  const errorIconColor = row.warnings?.find(w => w.type === SalaryWarningType.INCOMPATIBLE_SALARY_COMPONENTS) ? "error" : "warning";
  const tableSelectionText = formatTesSalaryTableSelectionSummary(row.raiseDateSalary);
  // console.log("warningText", warningText);
  // console.log("warnings", row?.warnings?.map(w => w.type).join(", "));
  // console.log(`in load row ${uiRowToId(row)} is now unclear ${row.unclear}`)
  return (
    <Fragment key={row.id}>
      <TableRow
        sx={{
          "& > *": { borderBottom: "unset" },
          backgroundColor: mainRowBackground,
        }}
      >
        <TableCell>
          <Checkbox
            checked={isSelected}
            onChange={onSelect}
            disabled={row.status === SalaryIncreaseStatus.SUCCESS}
          />
        </TableCell>
        <TableCell>
          <Box sx={{ flexGrow: 1, display: "flex", alignItems: "center" }}>
            <IconButton
              sx={{
                visibility: !isEmpty(row.afterRaiseSalaryPerDate)
                  ? "visible"
                  : "hidden",
              }}
              size="small"
              onClick={() => setOpen(!open)}
            >
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
            {!isEmpty(row.warnings) && (
              <Tooltip title={warningText}>
                <ErrorOutlineIcon color={errorIconColor} fontSize="small" />
              </Tooltip>
            )}
          </Box>
        </TableCell>
        <TableCell>
          <Button
            // disabled={disabled}
            component={Link}
            target="_blank"
            to={`/home/talents/${row.talentId}/profile`}
            style={{ paddingLeft: "0px", paddingRight: "0px" }}
            // onClick={() => {
            //   dispatch(setContinousEmploymentId(continuousEmploymentId || ""));
            // }}
            endIcon={
              <ArrowRight
                sx={{
                  height: "16px",
                  width: "16px",
                  color: (theme) =>
                    // disabled ? "text.disabled" :
                    theme.palette.primary.main,
                }}
              />
            }
          >
            <Typography variant="header" fontWeight="500" fontSize="12px">
              {/* {t("talentProfile:employmentAdditionalInfo")} */}
              {`${row.firstName} ${row.lastName}`}
            </Typography>
          </Button>
        </TableCell>
        <TableCell>{row.workTitle.name}</TableCell>
        <TableCell>
          {format(new Date(row.raiseDateSalary.date), leadingZerosDateFormat)}
        </TableCell>
        <TableCell>{tableSelectionText}</TableCell>
        <TableCell>{row.raiseDateContEmpExists ? "On" : "Ei"}</TableCell>
        <SalaryTableCells
          row={row}
          index={index}
          salaryKeys={raiseDateSalaryKeys}
        />
        <TableCell>{renderRowStatus(row.status)}</TableCell>
        <TableCell>
          <Tooltip
            title={row.unclear ? "This row is marked as unclear" : "This row is marked as clear"}>
            <Checkbox
              checked={row.unclear}
              onChange={() => setRowClearance(row)}
              disabled={row.status === SalaryIncreaseStatus.SUCCESS}
            />
          </Tooltip>
        </TableCell>
        <TableCell>{row.hrRepresentative?.name ?? null}</TableCell>
      </TableRow>
      <BulkSalaryIncreaseCollapsedRows
        open={open}
        row={row}
        afterRaiseDateSalaryPerDateKeys={afterRaiseDateSalaryPerDateKeys}
      />
    </Fragment>
  );
};

export default BulkSalaryIncreaseShiftSalaryRow;
